import {
  GameConfig,
  GameElements,
  PriceFetchStatus,
  PricePoint,
  HighlightSquare,
  BetRecord,
  BetStatus,
} from "./types";
import * as echarts from "echarts";

export class ETHGame {
  private readonly INITIAL_BALANCE: number = 2000;
  private isRunning: boolean = true; // Changed to true by default
  private currentIdx: number = 0;
  private currentPrice: number = 4000; // Initial ETH price
  private pricePoints: PricePoint[] = [];
  private priceUpdateInterval: number | null = null;
  private prevYAxisRange: { min: number; max: number } = {
    min: 100000,
    max: -10000,
  };

  // Price fetch status
  private priceStatus: PriceFetchStatus = {
    isConnected: false,
    lastUpdate: 0,
    errorCount: 0,
  };

  // Game configuration
  private readonly config: GameConfig = {
    priceGridSize: 1, // Price grid size (USD)
    priceUpdateRate: 1000, // Price update frequency (milliseconds) - 1 second
    xAxisRange: 20, // X-axis range
    minXAxis: 0, // Min X-axis value
    moveSpeedOnXAxis: 0.1, // Move speed on X-axis
    betAmount: 10, // Bet amount per bet
    distanceMultiplier: 2, // Distance multiplier for calculating odds
  };

  // DOM elements
  private elements: GameElements = {
    gridContainer: null,
    currentPriceEl: null,
    balanceEl: null,
    historyPanelEl: null,
    historyListEl: null,
    betAmountEl: null,
    gridSizeSlider: null,
    gridSizeValue: null,
    distanceMultiplierInput: null,
  };

  // Price status display element
  private priceStatusEl: HTMLElement | null = null;

  // ECharts related
  private chart: echarts.ECharts | null = null;

  // Base chart configuration
  private baseOption: any = null;

  // Add betting record
  private clickedSquares: HighlightSquare[] = [];

  // Balance management
  private balance: number = this.INITIAL_BALANCE;

  // Betting history
  private betHistory: BetRecord[] = [];

  // Current filter for history
  private currentFilter: string = "all";

  // Popup queue management
  private popupQueue: HTMLElement[] = [];
  private readonly POPUP_SPACING = 0; // Space between popups
  private readonly POPUP_HEIGHT = 60; // Approximate height of each popup

  /**
   * Initialize base chart configuration
   */
  private initBaseOption(): void {
    this.baseOption = {
      backgroundColor: "rgb(26, 29, 41)",
      grid: {
        left: 60,
        right: 60,
        top: 40,
        bottom: 40,
      },
      xAxis: {
        type: "value",
        min: this.config.minXAxis,
        max: this.config.minXAxis + this.config.xAxisRange,
        interval: 1,
        splitLine: {
          show: true,
          lineStyle: {
            color: "#676d7c50",
            type: "dashed",
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
          color: "rgb(177, 182, 198)",
        },
      },
      yAxis: [
        {
          type: "value",
          ...this.updateYAxisRange(),
          interval: this.config.priceGridSize,
          splitLine: {
            show: true,
            lineStyle: {
              color: "#676d7c50",
              type: "dashed",
            },
          },
          axisLabel: {
            show: true,
            color: "rgba(177, 182, 198, 0.9)",
            formatter: (value: number) => {
              return `$${+value.toFixed(2)}`;
            },
          },
          position: "left",
        },
        {
          type: "value",
          ...this.updateYAxisRange(),
          interval: this.config.priceGridSize,
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: true,
            color: "rgba(177, 182, 198, 0.9)",
            formatter: (value: number) => {
              return `$${+value.toFixed(2)}`;
            },
          },
          position: "right",
        },
      ],
      series: [
        {
          name: "Price Line",
          type: "line",
          data: [[0, this.currentPrice]], // Add initial data point
          smooth: false,
          symbol: false,
          itemStyle: {
            color: "transparent",
            borderWidth: 0,
          },
          lineStyle: {
            color: "#ffb017",
            width: 2,
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(255, 176, 23, 0.2)" },
                { offset: 1, color: "rgba(255, 176, 23, 0)" },
              ],
            },
          },
          z: 10,
        },
        {
          name: "Betting Squares",
          type: "scatter",
          data: [],
          animation: false,
          symbol: "rect",
          symbolSize: this.getHighlightSize(),
          itemStyle: {
            borderWidth: 0,
            borderColor: "transparent",
          },
          label: {
            show: true,
            position: "inside",
            formatter: (params: any) => {
              const square = this.clickedSquares[params.dataIndex];
              if (square?.hit) {
                return [
                  `+$${+(this.config.betAmount * square.multiplier).toFixed(
                    2
                  )}`,
                  `${+square.multiplier.toFixed(2)}x`,
                  `$${+square.y.toFixed(2)}`,
                ].join("\n");
              }

              if (square) {
                return [
                  `$${this.config.betAmount}`,
                  `${+square.multiplier.toFixed(2)}x`,
                  `$${+square.y.toFixed(2)}`,
                ].join("\n");
              }

              return "";
            },
            color: "rgb(177, 182, 198)",
            fontSize: 11,
            fontWeight: "bold",
          },
        },
      ],
    };
  }

  public async init(): Promise<void> {
    this.initElements();
    this.loadBalance();
    this.loadBetHistory();
    await this.fetchETHPrice();

    this.updateBalanceDisplay();

    this.createChart();

    // Start the game automatically
    this.startGame();

    // Add page visibility change listener
    this.addPageVisibilityListener();

    // Initialize history panel
    this.initHistoryPanel();

    // Initialize grid size control
    this.initGridSizeControl();
    this.initDistanceMultiplierControl();
  }

  /**
   * Initialize grid size control slider
   */
  private initGridSizeControl(): void {
    if (!this.elements.gridSizeSlider || !this.elements.gridSizeValue) return;

    // Set initial value
    this.elements.gridSizeSlider.value = this.config.priceGridSize.toString();
    this.elements.gridSizeValue.textContent =
      this.config.priceGridSize.toString();

    // Add event listener for slider changes
    this.elements.gridSizeSlider.addEventListener("input", (e) => {
      const target = e.target as HTMLInputElement;
      const newValue = parseFloat(target.value);

      // Update the config
      this.config.priceGridSize = newValue;

      // Update the display
      if (this.elements.gridSizeValue) {
        this.elements.gridSizeValue.textContent = newValue.toString();
      }

      this.prevYAxisRange = {
        min: 100000,
        max: -10000,
      };

      // Update chart with new grid size
      this.updateChartWithNewGridSize();
    });
  }

  /**
   * Initialize distance multiplier control input
   */
  private initDistanceMultiplierControl(): void {
    if (!this.elements.distanceMultiplierInput) return;

    // Get the display element for the value
    const valueDisplay = document.getElementById("distanceMultiplierValue");

    // Set initial value
    this.elements.distanceMultiplierInput.value =
      this.config.distanceMultiplier.toString();
    if (valueDisplay) {
      valueDisplay.textContent = this.config.distanceMultiplier.toString();
    }

    // Add event listener for input changes
    this.elements.distanceMultiplierInput.addEventListener("input", (e) => {
      const target = e.target as HTMLInputElement;
      const newValue = parseFloat(target.value);

      // Update the config
      this.config.distanceMultiplier = newValue;

      // Update the display
      if (valueDisplay) {
        valueDisplay.textContent = newValue.toString();
      }

      console.log(`Distance multiplier updated to: ${newValue}`);
    });
  }

  private initElements(): void {
    this.elements.gridContainer = document.getElementById("gridContainer");
    this.elements.currentPriceEl = document.getElementById("currentPrice");
    this.priceStatusEl = document.getElementById("priceStatus");

    // Add balance element
    this.elements.balanceEl = document.getElementById("balance");

    // Add history panel elements
    this.elements.historyPanelEl = document.getElementById("historyPanel");
    this.elements.historyListEl = document.getElementById("historyList");
    this.elements.betAmountEl = document.getElementById("betAmount");

    // Add grid size control elements
    this.elements.gridSizeSlider = document.getElementById(
      "gridSizeSlider"
    ) as HTMLInputElement;
    this.elements.gridSizeValue = document.getElementById("gridSizeValue");

    // Add distance multiplier control element
    this.elements.distanceMultiplierInput = document.getElementById(
      "distanceMultiplierInput"
    ) as HTMLInputElement;
  }

  /**
   * Initialize history panel functionality
   */
  private initHistoryPanel(): void {
    // Initialize filter buttons
    const filterBtns = document.querySelectorAll(".filter-btn");
    filterBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        const filter = target.dataset.filter;
        if (filter) {
          this.setFilter(filter);
        }
      });
    });

    this.updateHistoryDisplay();
  }

  /**
   * Set current filter and update display
   */
  private setFilter(filter: string): void {
    this.currentFilter = filter;

    // Update active button state
    const filterBtns = document.querySelectorAll(".filter-btn");
    filterBtns.forEach((btn) => {
      const target = btn as HTMLElement;
      if (target.dataset.filter === filter) {
        target.classList.add("active");
      } else {
        target.classList.remove("active");
      }
    });

    this.updateHistoryDisplay();
  }

  /**
   * Load balance from localStorage
   */
  private loadBalance(): void {
    const savedBalance = localStorage.getItem("ethGameBalance");
    if (savedBalance) {
      this.balance = parseFloat(savedBalance);
    } else {
      this.balance = this.INITIAL_BALANCE; // Default balance
      this.saveBalance();
    }

    if (this.elements.betAmountEl) {
      this.elements.betAmountEl.textContent = `(Bet: $${this.config.betAmount})`;
    }
  }

  /**
   * Save balance to localStorage
   */
  private saveBalance(): void {
    localStorage.setItem("ethGameBalance", this.balance.toString());
  }

  /**
   * Load bet history from localStorage
   */
  private loadBetHistory(): void {
    const savedHistory = localStorage.getItem("ethGameBetHistory");
    if (savedHistory) {
      try {
        this.betHistory = JSON.parse(savedHistory);
        // Ensure all records have status field for backward compatibility
        this.betHistory.forEach((record) => {
          if (!record.status) {
            record.status = record.hit ? BetStatus.HIT : BetStatus.MISS;
          }
        });
      } catch (error) {
        console.error("Failed to parse bet history:", error);
        this.betHistory = [];
      }
    }
  }

  /**
   * Save bet history to localStorage
   */
  private saveBetHistory(): void {
    localStorage.setItem("ethGameBetHistory", JSON.stringify(this.betHistory));
  }

  /**
   * Update balance display
   */
  private updateBalanceDisplay(): void {
    if (this.elements.balanceEl) {
      this.elements.balanceEl.textContent = `$${this.balance.toFixed(2)}`;
    }
  }

  /**
   * Update history display
   */
  private updateHistoryDisplay(): void {
    if (!this.elements.historyListEl) return;

    if (this.betHistory.length === 0) {
      this.elements.historyListEl.innerHTML =
        '<div class="history-empty">No betting history yet</div>';
      return;
    }

    // Sort history by timestamp (newest first)
    const sortedHistory = [...this.betHistory].sort(
      (a, b) => b.timestamp - a.timestamp
    );

    // Filter history based on current filter
    let filteredHistory = sortedHistory;
    if (this.currentFilter !== "all") {
      filteredHistory = sortedHistory.filter((record) => {
        if (this.currentFilter === "pending") {
          return record.status === BetStatus.PENDING;
        } else if (this.currentFilter === "hit") {
          return record.status === BetStatus.HIT;
        } else if (this.currentFilter === "miss") {
          return record.status === BetStatus.MISS;
        }
        return true;
      });
    }

    if (filteredHistory.length === 0) {
      this.elements.historyListEl.innerHTML =
        '<div class="history-empty">No records match the current filter</div>';
      return;
    }

    this.elements.historyListEl.innerHTML = filteredHistory
      .map((record) => {
        const date = new Date(record.timestamp);
        const timeStr = date.toLocaleTimeString();
        const dateStr = date.toLocaleDateString();

        // Determine status class and display text
        let statusClass = "pending";
        let amountClass = "pending";
        let amountText = `$${record.betAmount.toFixed(2)}`;
        let statusText = "Pending";

        if (record.status === BetStatus.HIT) {
          statusClass = "hit";
          amountClass = "win";
          amountText = `+$${record.winAmount?.toFixed(2)}`;
          statusText = "Hit";
        } else if (record.status === BetStatus.MISS) {
          statusClass = "miss";
          amountClass = "loss";
          amountText = `-$${record.betAmount.toFixed(2)}`;
          statusText = "Miss";
        }

        return `
        <div class="history-item ${statusClass}">
          <div class="history-main-content">
            <div class="history-info">
              <div class="history-time">${dateStr} ${timeStr}</div>
              <div class="history-bet">Bet: $${+record.betAmount.toFixed(
                2
              )}</div>
              <div class="history-target">Target: $${+record.targetPrice.toFixed(
                2
              )} at ${record.targetTime}s (${+record.multiplier?.toFixed(
          2
        )}x)</div>
            </div>
            <div class="history-result">
              <div class="history-status ${statusClass}">${statusText}</div>
              <div class="history-amount ${amountClass}">${amountText}</div>
              <div class="history-balance">Balance: $${+record.balanceAfterResult.toFixed(
                2
              )}</div>
            </div>
          </div>
        </div>
      `;
      })
      .join("");
  }

  /**
   * Add page visibility change listener to pause/resume game
   */
  private addPageVisibilityListener(): void {
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        // Page is hidden, pause the game
        this.pauseGame();
      } else {
        // Page is visible, resume the game
        this.resumeGame();
      }
    });
  }

  /**
   * Pause the game when page is hidden
   */
  private pauseGame(): void {
    if (this.isRunning) {
      this.isRunning = false;

      // Clear intervals
      if (this.priceUpdateInterval) {
        clearInterval(this.priceUpdateInterval);
        this.priceUpdateInterval = null;
      }

      console.log("⏸️ Game paused - page hidden");
    }
  }

  /**
   * Resume the game when page becomes visible
   */
  private resumeGame(): void {
    if (!this.isRunning) {
      this.isRunning = true;

      // Resume price updates
      this.startPriceUpdates();

      console.log("▶️ Game resumed - page visible");
    }
  }

  /**
   * Create ECharts chart for drawing price line
   */
  private createChart(): void {
    const container = this.elements.gridContainer;

    // Wait for DOM update before initializing chart
    setTimeout(() => {
      if (container) {
        // Initialize base configuration
        this.initBaseOption();

        // Initialize ECharts chart
        this.chart = echarts.init(container);

        // Set chart configuration
        this.chart.setOption(this.baseOption);

        // Force chart resize
        this.chart.resize();

        // Wait for chart rendering to complete before binding events
        setTimeout(() => {
          this.addChartClickEvent();
          // Update chart after initialization to ensure highlight square size is correct
          this.updateChartSeries();
        }, 100);
      } else {
        console.log("⚠️ Chart container not found");
      }
    }, 100);

    // Listen for window resize to adjust chart size
    window.addEventListener("resize", () => {
      if (this.chart && this.elements.gridContainer) {
        this.chart.resize();

        this.updateChartSeries();
      }
    });
  }

  /**
   * Add chart click event listener for betting
   */
  private addChartClickEvent(): void {
    if (!this.chart) {
      console.log("⚠️ Chart not initialized, cannot bind betting events");
      return;
    }

    this.chart.getZr().on("click", (params) => {
      console.log("🎯 Betting click event triggered:", params);
      this.handleChartClick(params);
    });
  }

  /**
   * Handle chart click events - now implements betting logic
   */
  private handleChartClick(params: any): void {
    let clickX: number, clickY: number;

    if (params.offsetX !== undefined && params.offsetY !== undefined) {
      // Click in blank area, convert pixel coordinates to data coordinates
      try {
        const pointInGrid = this.chart!.convertFromPixel({ seriesIndex: 0 }, [
          params.offsetX,
          params.offsetY,
        ]);
        if (pointInGrid && Array.isArray(pointInGrid)) {
          clickX = pointInGrid[0];
          clickY = pointInGrid[1];
        } else {
          console.log("⚠️ Cannot convert click coordinates");
          return;
        }
      } catch (error) {
        console.log("⚠️ Coordinate conversion failed:", error);
        return;
      }
    } else if (params.value && Array.isArray(params.value)) {
      // If clicking on data point
      clickX = params.value[0];
      clickY = params.value[1];
    } else {
      console.log("⚠️ Cannot get click coordinates, params:", params);
      return;
    }

    if (clickX < 0) {
      return;
    }

    const xVal = Math.floor(clickX);
    const yVal =
      Math.floor(clickY / this.config.priceGridSize) *
      this.config.priceGridSize;

    // Check if this is a valid betting position (not in the past)
    const currentTimePoint = this.currentIdx * this.config.moveSpeedOnXAxis;
    if (xVal <= currentTimePoint) {
      this.showBettingError("Cannot bet on past time!");
      return;
    }

    // Check if user has enough balance
    if (this.balance < this.config.betAmount) {
      this.showBettingError("Insufficient balance!");
      return;
    }

    const existingIndex = this.clickedSquares.findIndex(
      (square) => square.x === xVal && square.y === yVal
    );

    if (existingIndex === -1) {
      // Place bet
      this.placeBet(xVal, yVal);
    } else {
      this.showBettingError("Already bet on this cell!");
    }

    // Update chart to display highlight squares
    this.updateChartSeries();
  }

  /**
   * Place a bet
   */
  private placeBet(x: number, y: number): void {
    // Deduct bet amount from balance
    this.balance -= this.config.betAmount;
    this.saveBalance();

    // Calculate multiplier based on price distance from current price
    // Find the closer edge of the betting square to current price
    const priceStart = y;
    const priceEnd = y + this.config.priceGridSize;

    // Calculate distance to the closer edge
    let priceDistance: number;
    if (this.currentPrice <= priceStart) {
      // Current price is below the betting square
      priceDistance = priceStart - this.currentPrice;
    } else if (this.currentPrice >= priceEnd) {
      // Current price is above the betting square
      priceDistance = this.currentPrice - priceEnd;
    } else {
      // Current price is within the betting square
      priceDistance = 0;
    }

    // Calculate multiplier: price distance * distance multiplier
    // Use Math.floor to round down as requested
    const multiplier =
      priceDistance * Math.floor(this.config.distanceMultiplier) + 1;

    // Debug information
    console.log(`🎯 Bet placed:`, {
      currentPrice: this.currentPrice,
      bettingSquarePriceRange: [priceStart, priceEnd],
      priceDistance: priceDistance,
      distanceMultiplier: this.config.distanceMultiplier,
      calculatedMultiplier: multiplier,
    });

    // Create betting square with bet amount and multiplier
    const square: HighlightSquare = {
      x,
      y,
      timestamp: Date.now(),
      betAmount: this.config.betAmount,
      multiplier: multiplier,
    };

    this.clickedSquares.push(square);

    // Add to bet history
    const betRecord: BetRecord = {
      id: `bet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      betAmount: this.config.betAmount,
      targetPrice: y,
      targetTime: x,
      status: BetStatus.PENDING,
      hit: false,
      balanceAfterBet: this.balance,
      balanceAfterResult: this.balance,
      multiplier: multiplier,
    };

    this.betHistory.push(betRecord);
    this.saveBetHistory();

    // Update displays
    this.updateBalanceDisplay();
    this.updateHistoryDisplay();

    // Show success message with multiplier
    this.showBettingSuccess(x, y, multiplier);

    // Add balance change effect
    if (this.elements.balanceEl) {
      this.elements.balanceEl.classList.add("balance-change");
      setTimeout(() => {
        this.elements.balanceEl?.classList.remove("balance-change");
      }, 600);
    }
  }

  /**
   * Show congratulations popup when player wins
   */
  private showCongratulationsPopup(): void {
    // Create popup container
    const popup = document.createElement("div");
    popup.className = "popup-success";

    // Find the most recently hit square to get its multiplier
    const hitSquare = this.clickedSquares.find(
      (square) =>
        square.hit &&
        square.hitTime ===
          Math.max(
            ...this.clickedSquares
              .filter((s) => s.hit)
              .map((s) => s.hitTime || 0)
          )
    );
    const multiplier = hitSquare?.multiplier || 1;
    const winAmount = this.config.betAmount * multiplier;

    popup.textContent = `🎉 Congratulations, You Won $${winAmount.toFixed(
      2
    )}! (${multiplier.toFixed(2)}x)`;

    // Add to page
    document.body.appendChild(popup);

    // Add to queue system
    this.addPopupToQueue(popup);
  }

  /**
   * Show betting error message
   */
  private showBettingError(message: string): void {
    // Create error message element
    const errorEl = document.createElement("div");
    errorEl.className = "popup-error";
    errorEl.textContent = message;

    // Add to page
    document.body.appendChild(errorEl);

    // Add to queue system
    this.addPopupToQueue(errorEl);
  }

  /**
   * Show betting success message
   */
  private showBettingSuccess(x: number, y: number, multiplier: number): void {
    console.log("showBettingSuccess", x, y);
    // Create success message element
    const successEl = document.createElement("div");
    successEl.className = "popup-success";
    successEl.textContent = `Bet placed at price $${+y.toFixed(
      2
    )} with multiplier ${multiplier.toFixed(2)}`;

    // Add to page
    document.body.appendChild(successEl);

    // Add to queue system
    this.addPopupToQueue(successEl);
  }

  private startGame(): void {
    this.isRunning = true;
    this.startPriceUpdates();
  }

  private startPriceUpdates(): void {
    this.priceUpdateInterval = window.setInterval(() => {
      this.fetchETHPrice();
    }, this.config.priceUpdateRate);
  }

  /**
   * Fetch ETH real-time price from multiple API sources
   */
  private async fetchETHPrice(): Promise<void> {
    try {
      // Try multiple API sources to avoid rate limits
      const price = await this.tryMultipleAPIs();

      if (price > 0) {
        this.updatePrice(price, true);
      } else {
        throw new Error("All APIs failed");
      }
    } catch (error) {
      console.error("Failed to fetch ETH price:", error);
      this.handlePriceError(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }

  /**
   * Try multiple API sources
   */
  private async tryMultipleAPIs(): Promise<number> {
    const apis = [
      // Binance API - no rate limit
      () =>
        fetch("https://api.binance.com/api/v3/ticker/price?symbol=ETHUSDT")
          .then((res) => res.json())
          .then((data) => parseFloat(data.price)),

      // CoinGecko API - backup
      () =>
        fetch(
          "https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd"
        )
          .then((res) => res.json())
          .then((data) => data.ethereum.usd),

      // Coinbase API - backup
      () =>
        fetch("https://api.coinbase.com/v2/prices/ETH-USD/spot")
          .then((res) => res.json())
          .then((data) => parseFloat(data.data.amount)),

      // Kraken API - backup
      () =>
        fetch("https://api.kraken.com/0/public/Ticker?pair=ETHUSD")
          .then((res) => res.json())
          .then((data) => parseFloat(data.result.XETHZUSD.c[0])),
    ];

    // Try each API in sequence
    for (const api of apis) {
      try {
        const price = await api();
        if (price && price > 0) {
          console.log("Successfully got price from API:", price);
          return price;
        }
      } catch (error) {
        console.warn("API call failed, trying next:", error);
        continue;
      }
    }

    throw new Error("All APIs failed");
  }

  /**
   * Handle price errors
   */
  private handlePriceError(error: string): void {
    this.priceStatus.isConnected = false;
    this.priceStatus.errorCount++;
    this.priceStatus.lastError = error;

    // If consecutive failures, use simulated price as fallback
    if (this.priceStatus.errorCount > 3) {
      this.fallbackToSimulatedPrice();
    }

    this.updatePriceDisplay();
  }

  /**
   * Fallback simulated price generation
   */
  private fallbackToSimulatedPrice(): void {
    const volatility = 0.01; // Reduce volatility
    const change = (Math.random() - 0.5) * 2 * volatility * this.currentPrice;
    this.currentPrice = Math.max(100, this.currentPrice + change);
    this.updatePriceDisplay();
  }

  /**
   * Update price and handle related logic
   */
  private updatePrice(newPrice: number, isRealTime: boolean = true): void {
    this.currentPrice = newPrice;

    // Price axis range now handled automatically by ECharts

    // Update status
    this.priceStatus.isConnected = isRealTime;
    this.priceStatus.lastUpdate = Date.now();
    if (isRealTime) {
      this.priceStatus.errorCount = 0;
      this.priceStatus.lastError = undefined;
    }

    // Update display
    this.updatePriceDisplay();

    this.addPricePoint();
  }

  /**
   * Check if new data hits betting squares
   */
  private checkHighlightHit(newPrice: number): void {
    // Check each betting square
    for (let i = this.clickedSquares.length - 1; i >= 0; i--) {
      const square = this.clickedSquares[i];

      // Skip already hit squares
      if (square.hit) {
        continue;
      }

      // Check if the current price is within the time range of this betting square
      // A betting square covers a time range from x to x+1 (one grid unit)
      const currentTime = this.currentIdx * this.config.moveSpeedOnXAxis;
      const squareTimeStart = square.x;
      const squareTimeEnd = square.x + 1; // One grid unit width

      // Check if the square's time window has completely passed
      if (currentTime >= squareTimeEnd) {
        // Time window has passed without hitting, mark as missed
        this.markBetAsMissed(square);
        continue;
      }

      // Only check if current time is within the square's time range
      if (currentTime < squareTimeStart) {
        continue;
      }

      // Calculate price range represented by betting square (y coordinate increases by priceGridSize)
      const priceStart = square.y;
      const priceEnd = square.y + this.config.priceGridSize;

      // Check if new data is within betting square
      if (newPrice >= priceStart && newPrice < priceEnd) {
        this.hitHighlight(i);
        console.log(
          `🎯 Bet hit! Square (${square.x}, ${square.y})! Price: $${newPrice}`
        );
      }
    }
  }

  /**
   * Handle highlight square hit
   */
  private hitHighlight(squareIndex: number): void {
    const square = this.clickedSquares[squareIndex];

    // Mark this square as hit
    if (!square.hit) {
      square.hit = true;
      square.hitTime = Date.now();

      // Calculate winnings
      const winAmount = square.betAmount * square.multiplier;
      square.winAmount = winAmount;

      // Add winnings to balance
      this.balance += winAmount;
      this.saveBalance();

      // Update bet history
      this.updateBetHistory(square, winAmount);

      // Update displays
      this.updateBalanceDisplay();
      this.updateHistoryDisplay();

      // Show congratulations popup
      this.showCongratulationsPopup();

      // Add balance change effect
      if (this.elements.balanceEl) {
        this.elements.balanceEl.classList.add("balance-change");
        setTimeout(() => {
          this.elements.balanceEl?.classList.remove("balance-change");
        }, 600);
      }
    }
  }

  /**
   * Update bet history when bet is hit
   */
  private updateBetHistory(square: HighlightSquare, winAmount: number): void {
    // Find the corresponding bet record
    const betRecord = this.betHistory.find(
      (record) =>
        record.targetPrice === square.y &&
        record.targetTime === square.x &&
        record.status === BetStatus.PENDING
    );

    if (betRecord) {
      betRecord.hit = true;
      betRecord.status = BetStatus.HIT;
      betRecord.winAmount = winAmount;
      betRecord.balanceAfterResult = this.balance;
      this.saveBetHistory();
    }
  }

  /**
   * Mark bet as missed when it passes the time without hitting
   */
  private markBetAsMissed(square: HighlightSquare): void {
    // Find the corresponding bet record
    const betRecord = this.betHistory.find(
      (record) =>
        record.targetPrice === square.y &&
        record.targetTime === square.x &&
        record.status === BetStatus.PENDING
    );

    if (betRecord) {
      betRecord.status = BetStatus.MISS;
      betRecord.balanceAfterResult = this.balance;
      this.saveBetHistory();
    }
  }

  // Remove all game over modal related methods since they're no longer needed
  // private showGameOverModal(finalScore: number): void { ... }
  // private closeGameOverModal(): void { ... }
  // private getPerformanceMessage(score: number): string { ... }

  /**
   * Add price point and update ECharts chart
   */
  private addPricePoint(): void {
    if (!this.chart) return;

    this.currentIdx++;
    // Create price point, time coordinate remains as actual seconds
    const pricePoint: PricePoint = {
      x: this.currentIdx,
      y: this.currentPrice,
      price: this.currentPrice,
      timestamp: Date.now(),
    };

    // Add to price point array
    this.pricePoints.push(pricePoint);

    // Limit price point count to avoid excessive memory usage
    if (this.pricePoints.length > 1000) {
      this.pricePoints.shift();
    }

    // Check if new data hits betting squares
    this.checkHighlightHit(this.currentPrice);

    const halfXCoordinate = this.config.xAxisRange / 2;

    if (
      this.pricePoints.length * this.config.moveSpeedOnXAxis >=
      halfXCoordinate
    ) {
      const shiftAmount = halfXCoordinate;

      // Shift all betting squares left by the calculated amount
      this.clickedSquares.forEach((square) => {
        square.x -= shiftAmount;
      });

      // Remove betting squares that have moved beyond left boundary
      for (let i = this.clickedSquares.length - 1; i >= 0; i--) {
        // If the betting square is out of view (to the left of the visible chart), delete it
        if (this.clickedSquares[i].x < this.config.minXAxis) {
          this.clickedSquares.splice(i, 1);
        }
      }

      // Reset price points array and current index
      this.pricePoints = [];
      this.currentIdx = 0;

      // Add current price point as the first point
      const newPricePoint: PricePoint = {
        x: 0,
        y: this.currentPrice,
        price: this.currentPrice,
        timestamp: Date.now(),
      };
      this.pricePoints.push(newPricePoint);

      console.log(
        `✅ Data shifted left by ${shiftAmount} units, pricePoints reset, currentIdx reset to 0`
      );
    }

    // Remove betting squares that are completely out of view
    for (let i = this.clickedSquares.length - 1; i >= 0; i--) {
      // Remove squares that are completely out of the visible area
      // Note: Missed bets are already handled in checkHighlightHit method
      if (this.clickedSquares[i].x < -1) {
        this.clickedSquares.splice(i, 1);
      }
    }

    // Update ECharts chart data
    this.updateChartSeries();
  }

  /**
   * Update price display
   */
  private updatePriceDisplay(): void {
    if (this.elements.currentPriceEl) {
      this.elements.currentPriceEl.textContent = `$${this.currentPrice}`;
    }

    if (this.priceStatusEl) {
      if (this.priceStatus.isConnected) {
        this.priceStatusEl.textContent = "🟢";
        this.priceStatusEl.title = "Real-time price";
      } else {
        this.priceStatusEl.textContent = "🟡";
        this.priceStatusEl.title = "Simulated price";
      }
    }
  }

  private updateYAxisRange(): {
    min: number;
    max: number;
  } {
    if (
      this.currentPrice >
        this.prevYAxisRange.min + this.config.priceGridSize * 2 &&
      this.currentPrice <
        this.prevYAxisRange.max - this.config.priceGridSize * 2
    ) {
      return this.prevYAxisRange;
    }

    const currPriceCeil =
      Math.floor(this.currentPrice / this.config.priceGridSize) *
      this.config.priceGridSize;
    const min = currPriceCeil - this.config.priceGridSize * 5;
    const max = currPriceCeil + this.config.priceGridSize * 5;

    this.prevYAxisRange = { min, max };

    return {
      min,
      max,
    };
  }

  /**
   * Update chart series data
   */
  private updateChartSeries(): void {
    if (!this.chart || !this.baseOption) return;

    const lineData = this.pricePoints.map((point) => {
      return [point.x * this.config.moveSpeedOnXAxis, point.price];
    });

    const bettingData = this.clickedSquares.map((square) => {
      const centerX = square.x + 0.5;
      const centerY = square.y + this.config.priceGridSize / 2;
      return [centerX, centerY];
    });

    const updateOption = {
      ...this.baseOption,
      yAxis: {
        ...this.baseOption.yAxis,
        ...this.updateYAxisRange(),
      },
      yAxis2: {
        ...this.baseOption.yAxis2,
        ...this.updateYAxisRange(),
      },
      series: [
        {
          data: lineData,
        },
        {
          symbolSize: this.getHighlightSize(),
          data: bettingData,
          itemStyle: {
            // Set color based on bet status
            color: (params: any) => {
              const square = this.clickedSquares[params.dataIndex];
              if (square && square.hit) {
                return "#72f23770"; // Show green after bet is hit
              }

              // Check if the bet has missed (time window has passed)
              const currentTime =
                this.currentIdx * this.config.moveSpeedOnXAxis;
              const squareTimeEnd = square.x + 1;

              if (currentTime >= squareTimeEnd) {
                return "#ff494950"; // Show red for missed bets
              }

              // Show yellow for pending bets (they can still hit within their time range)
              return "#e2b61c70";
            },
          },
        },
      ],
    };

    this.chart.setOption(updateOption);
  }

  /**
   * Update chart with new grid size
   */
  private updateChartWithNewGridSize(): void {
    if (!this.chart) return;

    // Re-initialize base option with new grid size
    this.initBaseOption();

    // Set new option
    this.chart.setOption(this.baseOption);

    // Force chart resize
    this.chart.resize();

    // Update chart series data to reflect new grid size
    this.updateChartSeries();
  }

  /**
   * Get price status information
   */
  public getPriceStatus(): PriceFetchStatus {
    return { ...this.priceStatus };
  }

  /**
   * Get clicked square records
   */
  public getClickedSquares(): HighlightSquare[] {
    return [...this.clickedSquares];
  }

  /**
   * Get current balance
   */
  public getBalance(): number {
    return this.balance;
  }

  /**
   * Get bet history
   */
  public getBetHistory(): BetRecord[] {
    return [...this.betHistory];
  }

  private getHighlightSize(): [number, number] {
    if (!this.chart) {
      return [0, 0]; // Default size
    }

    // Get chart pixel dimensions
    const chartWidth = this.chart.getWidth();
    const chartHeight = this.chart.getHeight();

    // Calculate pixel size of one grid cell
    const xAxisUnits = this.config.xAxisRange;
    const yAxisRange = this.updateYAxisRange();
    const yAxisUnits =
      (yAxisRange.max - yAxisRange.min) / this.config.priceGridSize;

    // Calculate pixel size of each grid cell
    const cellWidth = chartWidth / xAxisUnits;
    const cellHeight = chartHeight / yAxisUnits;

    // Return size covering one grid cell (slightly smaller to leave borders)
    const bettingSquareSize: [number, number] = [
      cellWidth * 0.9,
      cellHeight * 0.9,
    ];

    return bettingSquareSize;
  }

  /**
   * Add popup to queue and position it
   */
  private addPopupToQueue(popup: HTMLElement): void {
    // Add to queue
    this.popupQueue.push(popup);

    // Position the popup
    this.positionPopup(popup, this.popupQueue.length - 1);

    // Remove from queue after animation
    setTimeout(() => {
      this.removePopupFromQueue(popup);
    }, 2000);
  }

  /**
   * Position popup based on its position in queue
   */
  private positionPopup(popup: HTMLElement, index: number): void {
    const topPosition = 20 + index * (this.POPUP_HEIGHT + this.POPUP_SPACING);
    popup.style.top = `${topPosition}px`;
    popup.style.right = "20px";
  }

  /**
   * Remove popup from queue and reposition remaining popups
   */
  private removePopupFromQueue(popup: HTMLElement): void {
    const index = this.popupQueue.indexOf(popup);
    if (index > -1) {
      this.popupQueue.splice(index, 1);

      // Reposition remaining popups
      this.popupQueue.forEach((remainingPopup, newIndex) => {
        this.positionPopup(remainingPopup, newIndex);
      });
    }

    // Remove from DOM
    if (popup.parentNode) {
      popup.parentNode.removeChild(popup);
    }
  }
}
